
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, input, OnInit, output, inject, viewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TENANTS_INFO } from '@fitness-force/state';
import type { TENANT_MODEL, TENANTSINTERFACE } from '@fitness-force/models';
import { TreeNode } from 'primeng/api';
import { StyleClassModule } from 'primeng/styleclass';
import { TreeSelect, TreeSelectModule } from 'primeng/treeselect';

@Component({
  selector: 'multi-tenant-list',
  standalone: true,
  imports: [TreeSelectModule, StyleClassModule, ReactiveFormsModule],
  templateUrl: './multi-tenant-list.component.html',
  styleUrl: './multi-tenant-list.component.css',
  changeDetection: ChangeDetectionStrategy.Default
})
export class MultiTenantListComponent implements OnInit {
  private readonly store = inject(Store);
  cdr = inject(ChangeDetectorRef);
  router = inject(Router);

  readonly selectionMode = input<'single' | 'multiple' | 'checkbox'>('single');
  readonly showInActiveTenant = input<boolean>(true);
  readonly selectedTenantId = input<number>();
  readonly styleClass = input<string>("w-full h-full bg-gray-800 border-none header__tenant-list ");
  readonly isGlobal = input<boolean>(false);
  readonly class = input<string>('text-white');
  readonly setPreselectedTenant = input<boolean>(true);
  readonly emittedTenant = output<TENANT_MODEL>();
  readonly emittedTenants = output<TENANT_MODEL[]>();
  readonly unselectedTenant = output<TENANT_MODEL>();
  readonly treeSelect = viewChild<TreeSelect>('treeSelect');

  
  selectedTenantField: UntypedFormControl = new UntypedFormControl('');
  tenants: any[];
  filteredOptions: any[];
  tenantAccess: TENANT_MODEL[] = [];
  formGroup!: FormGroup;
  selectedNodes: any[] = [];
  formControlTenantId:number = 0;

  pageNames = [
    { name: 'tenants', url: '/admin/tenant-list' },
    { name: 'create-tenant', url: '/admin/tenant-list/create-tenant' },
    { name: 'company-settings', url: '/company-settings' },
    { name: 'company-settings-detail', url: '/company-settings/company-settings' },
  ];
  disableAllTenants:boolean = false;
  private storeSelectedTenantId = 0;

  constructor(){

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.handleRouteChange(event.url, this.storeSelectedTenantId);
        this.store.dispatch({ type: 'ROUTER_GO' });
      }
    });
  }

  ngOnInit(): void {
    this.formGroup = new FormGroup({
      selectedTenants: new FormControl()
  });
    this.store.select(TENANTS_INFO).subscribe((tenantState: TENANTSINTERFACE) => {
      if(this.showInActiveTenant() === false) this.tenantAccess = tenantState.accessed_tenant.filter((t) => t.status.toLowerCase() !== 'inactive');  
      else this.tenantAccess = tenantState.accessed_tenant;
      this.storeSelectedTenantId = tenantState.selected_tenant?.id ?? 0;
      this.getTreeViewData();
      const initialUrl = this.router.url;
      // Use store's selected_tenant directly to avoid flip-back from stale parent input during same tick
      const storeTenantId = tenantState.selected_tenant?.id;
      this.handleRouteChange(initialUrl, storeTenantId);
      this.syncSelectedTenantInForm(storeTenantId ?? this.selectedTenantId());
    });
  }

  private syncSelectedTenantInForm(tenantId?: number): void {
    if (!tenantId || tenantId <= 0) {
      if (this.setPreselectedTenant() === false) {
        this.formGroup.get('selectedTenants')?.setValue(null);
      }
      return;
    }

    const selectedNode = this.findNodeByTenantId(this.tenants, tenantId);
    if (selectedNode && this.setPreselectedTenant()) {
      this.formGroup.get('selectedTenants')?.setValue(selectedNode);
    } else if (this.setPreselectedTenant() === false) {
      this.formGroup.get('selectedTenants')?.setValue(null);
    }
    this.cdr.detectChanges();
  }

  handleRouteChange(url: string, storeTenantId?: number): void {
    const path = url.split('?')[0];
    const tenantId = storeTenantId ?? this.storeSelectedTenantId ?? this.selectedTenantId();
    const isGlobalRoute =
      this.pageNames.some((page) => page.url === path) ||
      path.startsWith('/company-settings');

    if (isGlobalRoute) {
      this.disableAllTenants = true;
      this.getTreeViewData();
      this.syncSelectedTenantInForm(tenantId);
    } else {
      this.disableAllTenants = false;
      this.getTreeViewData();
      this.syncSelectedTenantInForm(tenantId);
    }
  }

  getTreeViewData() {
    const treeViewData:any[] = [];
    if(this.tenantAccess && this.tenantAccess.length <= 15){
    this.tenantAccess.forEach((tenant) => {
      const tenantNode = {
        label: tenant.name_primary_language,
        tenantid: tenant.id,
        data: tenant,
        selectable: this.disableAllTenants ? this.disableAllTenants : tenant.status.toLowerCase() === 'inactive' ? false : true,
        styleClass: this.disableAllTenants ? 'p-disabled' : tenant.status.toLowerCase() === 'inactive' ? 'p-disabled' : '',
        children: []
      };
      treeViewData.push(tenantNode);
    });
      if(this.disableAllTenants)
      {
        treeViewData.push( { label: 'Global', tenantid: 0, data: {id: 0, name_primary_language: 'Global'}, selectable: true, styleClass: '', children: [] });
      }
      else if(treeViewData.findIndex(o => o.label.toLowerCase() == 'global') > -1){
        treeViewData.splice(treeViewData.findIndex(o => o.label.toLowerCase() == 'global')!,1); 
      }
    }
    else{
      const alltenantStateCodes = this.tenantAccess.map((tenant) => {
        return { code: tenant.state, name: tenant.state_name };
      });
      // const alltenantStateCodes = this.tenantAccess.map((tenant) =>  (code= tenant.state, name= tenant.state_name));
      const distinctStateCodes = alltenantStateCodes.filter((value, index, self) => {
        return self.findIndex(v => v.code === value.code) === index;
      });
  
      if(this.disableAllTenants)
         distinctStateCodes.push( { code: 'Global', name: 'Global' });
      else if(distinctStateCodes.findIndex(o => o.code.toLowerCase() == 'global') > -1){
        distinctStateCodes.splice(distinctStateCodes.findIndex(o => o.code.toLowerCase() == 'global'),1);
      }
  
      let i = 0;
      for (const state of distinctStateCodes) {
        const citiesInState = Array.from(new Set(
          this.tenantAccess.filter((tenant) => tenant.state === state.code)
            .map((tenant) => { return { code: tenant.city, name: tenant.city_name } })
        ));
  
        const distinctCitiesInState = citiesInState.filter((value, index, self) => {
          return self.findIndex(v => v.code === value.code) === index;
        });
  
        const stateNode = {
          key: i,
          label: state.name,
          icon: 'fa fa-globe',
          children: new Array
        };
        let j = 0;
        for (const city of distinctCitiesInState) {
          const tenantsInCity = this.tenantAccess.filter((tenant) => tenant.state === state.code && tenant.city === city.code)
            .map((tenant) => ({
              label: tenant.name_primary_language,
              tenantid: tenant.id,
              data: tenant,
              selectable: this.disableAllTenants ? this.disableAllTenants : tenant.status.toLowerCase() === 'inactive' ? false : true,
              styleClass: this.disableAllTenants ? 'p-disabled' : tenant.status.toLowerCase() === 'inactive' ? 'p-disabled' : '',
              children: []
            }));
  
          stateNode.children.push({
            key: j,
            label: city.name,
            icon: '',
            children: tenantsInCity
          });
          j++;
        }
        treeViewData.push(stateNode);
        i++;
      }
    }
    this.tenants =  treeViewData;
    this.filteredOptions = treeViewData
    this.filteredOptions = this.getFilteredOptions();
    this.cdr.detectChanges();
  }

  findNodeByTenantId(nodes: any[], tenantId: number): TreeNode | null {
    if(this.disableAllTenants){
      return nodes.find((node) => node.label === 'Global');
    }
    else{
      for (const node of nodes) {
        if (node.tenantid === tenantId) {
          return node;
        }
        if (node.children) {
          const found = this.findNodeByTenantId(node.children, tenantId);
          if (found) {
            return found;
          }
        }
      }
      return null;
    }
   
  }

  async onNodeSelect(event:any)
  {
    const selectionMode = this.selectionMode();
    if(selectionMode === 'multiple' || selectionMode === 'checkbox')
    {
      const selectedTenantNodes = this.formGroup.controls.selectedTenants.value as TreeNode[];
      console.log("Selected tenant nodes", selectedTenantNodes.map((node) => node.data));
      this.emittedTenants.emit(selectedTenantNodes.map((node) => node.data));
      this.cdr.detectChanges();
    }
    else {
      if(event.node.children.length === 0)
      {
        const selectedNode = this.findNodeByTenantId(this.tenants, event.node.data.id);
        if (selectedNode) {
            this.formGroup.get('selectedTenants')?.setValue(selectedNode);
        }
        await this.timeout(100);
        this.emittedTenant.emit(event.node.data);
      }
      else{
        const tenantId = this.storeSelectedTenantId || this.selectedTenantId();
        if (tenantId) {
          const selectedNode = this.findNodeByTenantId(this.tenants, tenantId);
          if (selectedNode) {
              this.formGroup.get('selectedTenants')?.setValue(selectedNode);
          }
        }
        event.node.expanded = !event.node.expanded;
        this.toggleChildNodes(event.node, event.node.expanded);
      }
    } 
    this.cdr.detectChanges();
  }

  onNodeUnselect(event:any)
  {
    console.log("Unselected tenant nodes", event.node.data);
    this.unselectedTenant.emit(event.node.data.id);
    this.cdr.detectChanges();
  }

  toggleChildNodes(node: TreeNode, expand: boolean) {
    if (node.children) {
      node.children.forEach(child => {
        child.expanded = expand;
        this.toggleChildNodes(child, expand); // Recursively toggle child nodes
      });
    }
  }

  filterLastChildren(treeData: TreeNode[]): TreeNode[] {
    const lastChildren: TreeNode[] = [];

    function traverse(node: TreeNode) {
      if (node.children && node.children.length > 0) {
        // node.children.forEach((child) => {
        //   traverse(child);
        // });
      } else {
        lastChildren.push(node);
      }
    }

    treeData.forEach((node) => {
      traverse(node);
    });
    return lastChildren;
  }

  getFilteredOptions() {
    const selectedTenants = this.formGroup.get('selectedTenants')?.value || [];
    // Ensure selectedTenants is an array
    const selectedTenantsArray = Array.isArray(selectedTenants) ? selectedTenants : [selectedTenants];
    if(this.disableAllTenants)
      return this.tenants.filter((tenant: any) => tenant.label.toLowerCase() !== 'global');
    else return this.tenants.filter((tenant: any) => !selectedTenantsArray.includes(tenant));
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
